import { createSlice, current } from '@reduxjs/toolkit';
import { LOADING_STATUS } from '@sonifi/wb-core/common/Constants';
import {
  findNodeById,
  findNodeParent,
  isNodeRoot,
} from '@sonifi/wb-tree/utils/helper';
import {
  createCategoryChildren,
  createTvVisibleChildren,
  findAndUpdateNodes,
  findNodePreviewHCTV,
  findNodesByPlatform,
  getHasUnpublishedChanges,
  getPublishStatusMessage,
  isBeyondMaxChildren,
} from './guestRequestMenuSliceHelper';
import { PLATFORMS } from '@sonifi/wb-screenbuilder/constants/wbScreenBuilderConstants';
import {
  isFailedJob,
  isIncompleteJob,
} from '@sonifi/wb-core/services/jobsHelper';
import { GR_NODE_TYPES } from '@sonifi/wb-guest-request/constants/wbGuestRequestConstants';
import { GROUP_DELIVERY_METHOD } from '@sonifi/wb-group/constants/wbGroupConstants';

const initialState = {
  addNodeFrom: null,
  background: null,
  canAddAdditional: false,
  guestRequest: null,
  hasUnpublishedChanges: false,
  hctvCategoryPreview: true,
  hctvSelectedPreviewNode: null,
  hctvVisibleChildren: [],
  isFailedJob: false,
  isIncompleteJob: false,
  isIntegratedOrdering: false,
  isOrderByOptions: false,
  isPublishing: false,
  lastPublishJob: null,
  lastPublished: null,
  lastPublishUser: null,
  loadingMenu: LOADING_STATUS.notLoading,
  nodeMenuSelected: null,
  nodePreviewParentSelected: null,
  nodePreviewSelected: null,
  nodeSelected: null,
  nodeSelectedParent: null,
  platform: PLATFORMS.tv,
  productSettings: null,
  publishStatusMessage: '',
  requests: [],
  saveInProgress: false,
  saveItemInProgress: false,
  selected: [],
  serviceCenterSettings: {},
  serviceMenu: {},
  showEmail: false,
  showHcTvOptions: [],
  showModal: false,
  showOptions: false,
  tvCategoryChildren: [],
  tvSelectedChild: null,
  tvVisibleChildren: null,
};

export const guestRequestMenuSlice = createSlice({
  name: 'guestRequestMenu',
  initialState,
  reducers: {
    setMenu: (state, action) => {
      const {
        background,
        guestRequest,
        lastPublished,
        lastPublishUser,
        lastPublishJob,
        platform,
        productSettings,
        serviceCenterSettings,
        serviceMenu,
      } = action.payload;
      state.background = background;
      state.guestRequest = guestRequest;
      state.serviceMenu = serviceMenu;
      state.productSettings = productSettings;
      state.isIntegratedOrdering =
        productSettings.delivery_method === GROUP_DELIVERY_METHOD.integrated;
      state.isOrderByOptions =
        productSettings.delivery_method === GROUP_DELIVERY_METHOD.onSite &&
        productSettings.order_by_option;
      state.serviceCenterSettings = serviceCenterSettings;
      state.displayItemImages = productSettings.display_category_images;
      state.platform = platform;
      const incompleteJob = lastPublishJob
        ? isIncompleteJob(lastPublishJob)
        : null;
      const failedJob = lastPublishJob ? isFailedJob(lastPublishJob) : null;
      state.publishStatusMessage = getPublishStatusMessage({
        lastPublishJob: lastPublished,
        isIncompleteJob: incompleteJob,
        isFailedJob: failedJob,
      });
      state.isIncompleteJob = incompleteJob;
      state.isFailedJob = failedJob;
      state.lastPublished = lastPublished;
      state.lastPublishUser = lastPublishUser;
      state.lastPublishJob = lastPublishJob;
      const lastPublishedDate = lastPublished ? new Date(lastPublished) : null;
      state.hasUnpublishedChanges = getHasUnpublishedChanges({
        serviceMenu,
        lastPublished: lastPublishedDate,
        productSettings,
        serviceCenterSettings,
      });
    },
    setServiceMenu(state, action) {
      const { serviceMenu } = action.payload;
      state.serviceMenu = serviceMenu;
    },
    setLoadingGuestRequestMenu(state, action) {
      state.loadingMenu = action.payload;
    },
    setSaveInProgress(state, action) {
      state.saveInProgress = action.payload;
    },
    setSaveItemInProgress(state, action) {
      state.saveItemInProgress = action.payload;
    },
    setShowModal(state, action) {
      state.showModal = action.payload;
    },
    setNodeSelectedForAdd(state, action) {
      const { fromNodeId } = action.payload;
      const { productSettings, serviceMenu } = current(state);
      // Make sure have the latest Node state from the Tree
      const fromNode = findNodeById({
        menu: serviceMenu.menu,
        nodeId: fromNodeId,
      });
      const type = isNodeRoot({ menu: serviceMenu.menu, nodeId: fromNode.id })
        ? GR_NODE_TYPES.serviceCategories
        : GR_NODE_TYPES.serviceIssues;
      const nodeSelected = { type, subject: { title: null, options: [] } };
      state.nodeSelected = nodeSelected;
      state.addNodeFrom = fromNode;
      state.nodeSelectedParent = fromNode;
      state.showEmail = productSettings.delivery_method === 'email';
      state.canAddAdditional = !isBeyondMaxChildren(
        fromNode?.children?.length + 1 || 0,
      );
    },
    setNodeSelected(state, action) {
      const { node } = action.payload;
      const { serviceMenu, productSettings } = current(state);
      const parent = findNodeParent({ menu: serviceMenu.menu, node });
      let nodeSelected = node;
      if (node.type === GR_NODE_TYPES.serviceIssues) {
        nodeSelected = {
          ...node,
          subject: {
            ...node.subject,
            options: node.subject.options.map((op) => ({
              ...op,
              showDetail: false,
            })),
          },
        };
      }
      state.nodeSelected = nodeSelected;
      state.nodeSelectedParent = parent;
      state.showEmail = productSettings.delivery_method === 'email';
    },
    setNodePreviewSelected(state, action) {
      const { node } = action.payload;
      const { serviceMenu, platform } = current(state);

      if (platform === PLATFORMS.hctv) {
        const { nodeFound, hctvCategoryPreview } = findNodePreviewHCTV(node);
        state.nodePreviewSelected = nodeFound;
        state.hctvCategoryPreview = hctvCategoryPreview;
        state.nodeMenuSelected = nodeFound;

        state.hctvVisibleChildren = hctvCategoryPreview
          ? [...(nodeFound.children?.filter((ch) => ch.active).slice(0, 5) || [])]
          : [];
        if (nodeFound && nodeFound.subject.options) {
          const selected = [];
          const requests = [];
          nodeFound.subject.options?.forEach((op) => {
            if (op.type === 'checkbox') {
              selected.push(false);
              requests.push(op.options[0]);
            }
            return false;
          });
          state.requests = requests;
          state.selected = selected;
          state.showHcTvOptions = requests.map((_) => false);
        }
      } else {
        // TV - Phone
        const { nodeParent, nodePreviewSelected } = findNodesByPlatform({
          menu: serviceMenu.menu,
          node,
          platform,
        });
        state.nodePreviewParentSelected = nodeParent;
        state.nodePreviewSelected = nodePreviewSelected;
        state.nodeMenuSelected = node;

        const tvVisibleChildren = createTvVisibleChildren({
          menu: serviceMenu.menu,
          nodeSelected: nodePreviewSelected,
        });
        state.tvVisibleChildren = tvVisibleChildren;
        const tvCategoryChildren = createCategoryChildren({
          selectedNodeChildren:
            nodePreviewSelected?.children?.filter((ch) => ch.active) || [],
          menuSelectedNodeIndex: 0,
        });
        state.tvCategoryChildren = tvCategoryChildren;
        state.tvSelectedChild =
          tvCategoryChildren.find((ch) => ch.id === node.id) ||
          tvCategoryChildren[0];
      }
    },
    moveDown(state) {
      const { nodePreviewSelected, tvSelectedChild } = current(state);
      let currentIndex = nodePreviewSelected.children.findIndex(
        (ch) => ch.id === tvSelectedChild?.id,
      );
      if (currentIndex < nodePreviewSelected.children.length - 1) {
        currentIndex++;
      }
      const selectedChild = nodePreviewSelected.children[currentIndex];
      const menuSelectedNodeIndex = currentIndex;
      state.tvCategoryChildren = createCategoryChildren({
        selectedNodeChildren: nodePreviewSelected?.children || [],
        menuSelectedNodeIndex,
      });
      state.tvSelectedChild = selectedChild;
    },
    setTvSelectedChild(state, action) {
      const { node } = action.payload;
      state.tvSelectedChild = node;
    },
    setShowOptions(state, action) {
      state.showOptions = action.payload;
    },
    setSelectedHcTvPreviewNode(state, action) {
      const { node } = action.payload;
      state.hctvSelectedPreviewNode = node;
    },
    setHcTvNextCategory(state) {
      const { serviceMenu, hctvSelectedPreviewNode } = current(state);
      const currentIndex = serviceMenu.menu.children.findIndex(
        (ch) => ch.id === hctvSelectedPreviewNode.id,
      );
      const nextIndex = currentIndex + 1;
      if (serviceMenu.menu.children.length > nextIndex) {
        state.hctvSelectedPreviewNode = serviceMenu.menu.children[nextIndex];
        if (nextIndex > 4) {
          state.hctvVisibleChildren = serviceMenu.menu.children.slice(
            nextIndex - 4,
            nextIndex + 1,
          );
        }
      }
    },
    setHcTvPreviewCategory(state) {
      const { serviceMenu, hctvSelectedPreviewNode, hctvVisibleChildren } =
        current(state);
      const currentIndex = hctvVisibleChildren.findIndex(
        (ch) => ch.id === hctvSelectedPreviewNode.id,
      );
      const prevIndex = currentIndex - 1;
      if (prevIndex > -1) {
        state.hctvSelectedPreviewNode = hctvVisibleChildren[prevIndex];
      } else {
        const actualIndex = serviceMenu.menu.children.findIndex(
          (ch) => ch.id === hctvSelectedPreviewNode.id,
        );
        if (actualIndex > 0) {
          state.hctvVisibleChildren = serviceMenu.menu.children.slice(
            actualIndex - 1,
            actualIndex + 4,
          );
          state.hctvSelectedPreviewNode = hctvVisibleChildren[0];
        }
      }
    },
    setShowHcTvOptions(state, action) {
      const { nodeIndex, show } = action.payload;
      const { showHcTvOptions } = current(state);
      state.showHcTvOptions = showHcTvOptions.map((ch, index) =>
        index === nodeIndex ? show : ch,
      );
    },
    setSelectMe(state, action) {
      const { optionIndex } = action.payload;
      const { selected } = current(state);
      const newSelected = [...selected];
      newSelected[optionIndex] = !newSelected[optionIndex];
      state.selected = newSelected;
    },
    updateMenuLastUpdateByNodes(state, action) {
      const { nodes, lastUpdated } = action.payload;
      const { serviceMenu } = current(state);

      let newServiceMenu = null;
      if (nodes.some((node) => node.id === serviceMenu.menu.subject.id)) {
        newServiceMenu = {
          ...serviceMenu,
          menu: {
            ...serviceMenu.menu,
            subject: {
              ...serviceMenu.menu.subject,
              last_updated: lastUpdated,
            },
          },
        };
      } else {
        const children = findAndUpdateNodes({
          children: serviceMenu.menu.children,
          nodes,
          lastUpdated,
        });
        newServiceMenu = {
          ...serviceMenu,
          menu: {
            ...serviceMenu.menu,
            children,
          },
        };
      }
      state.serviceMenu = newServiceMenu;
    },
    updateJobsStatus(state, action) {
      const { lastPublishJob, lastPublished, lastPublishUser } =
        action.payload;
      const { serviceMenu, productSettings, serviceCenterSettings } =
        current(state);

      const incompleteJob = lastPublishJob
        ? isIncompleteJob(lastPublishJob)
        : null;
      const failedJob = lastPublishJob ? isFailedJob(lastPublishJob) : null;
      state.publishStatusMessage = getPublishStatusMessage({
        lastPublishJob: lastPublished,
        isIncompleteJob: incompleteJob,
        isFailedJob: failedJob,
      });
      state.isIncompleteJob = incompleteJob;
      state.isFailedJob = failedJob;
      state.lastPublishJob = lastPublishJob;
      state.lastPublished = lastPublished;
      state.lastPublishUser = lastPublishUser;
      const lastPublishedDate = lastPublished ? new Date(lastPublished) : null;
      state.hasUnpublishedChanges = getHasUnpublishedChanges({
        serviceMenu,
        lastPublished: lastPublishedDate,
        productSettings,
        serviceCenterSettings,
      });
    },
    setPublishingStatus(state, action) {
      const { job } = action.payload;
      const incompleteJob = job ? isIncompleteJob(job) : null;
      state.isIncompleteJob = incompleteJob;
      state.lastPublishJob = job;
      state.lastPublished = job.last_updated;
      state.lastPublishUser = job._edited_by;
      state.isFailedJob = false;
      state.publishStatusMessage = getPublishStatusMessage({
        lastPublishJob: job,
        isIncompleteJob: incompleteJob,
        isFailedJob: false,
      });
      state.hasUnpublishedChanges = false;
    },
    checkUnpublishedChanges(state) {
      const {
        serviceMenu,
        serviceCenterSettings,
        lastPublished,
        productSettings,
      } = current(state);
      const lastPublishedDate = lastPublished ? new Date(lastPublished) : null;
      state.hasUnpublishedChanges = getHasUnpublishedChanges({
        serviceMenu,
        lastPublished: lastPublishedDate,
        productSettings,
        serviceCenterSettings,
      });
    },
    cleanGuestRequestMenuSlice() {
      return initialState;
    },
  },
});

export const {
  cleanGuestRequestMenuSlice,
  checkUnpublishedChanges,
  moveDown,
  setHcTvNextCategory,
  setHcTvPreviewCategory,
  setLoadingGuestRequestMenu,
  setMenu,
  setNodePreviewSelected,
  setNodeSelected,
  setNodeSelectedForAdd,
  setPublishingStatus,
  setSaveInProgress,
  setSaveItemInProgress,
  setSelectedHcTvPreviewNode,
  setSelectMe,
  setServiceMenu,
  setShowHcTvOptions,
  setShowModal,
  setShowOptions,
  setTvSelectedChild,
  updateJobsStatus,
  updateMenuLastUpdateByNodes,
} = guestRequestMenuSlice.actions;

export default guestRequestMenuSlice.reducer;
